import { useCallback, useMemo } from 'react';
import { AppName } from 'sl-api-connector';
import { ExportDropdownOptionIds } from 'src/components/BeaconV2Components/common/AppExportModal/exportDataConfigData';
import { useExportFileName } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicExports/TopicExportModal';
import {
  TopicExportResponse,
  topicSettingsServerProxy
} from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/serverProxy';
import { useBaseTopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import {
  BeaconTopicExportType,
  TopicExportRequestBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/TopicExportRequestBuilder';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { getAppName, shouldShowBeaconProTopics, shouldShowNewBeacon } from 'src/utils/app';
import { useAppSelector } from 'src/utils/Hooks';

interface TopicExportParams {
  isSeparatedByWeek: boolean;
  isIncludeComparison: boolean;
}

enum TrafficSubtabExportTypes {
  AD_CLICKS = 'GroupByKeywordNonTopicPaidTraffic',
  AD_CLICKS_BY_WEEK = 'GroupByKeywordByWeekNonTopicPaidTraffic',
  TOTAL_CLICKS = 'GroupByKeywordNonTopic',
  ORGANIC_CLICKS = 'GroupByKeywordNonTopic'
}

/**
 * Hook to encapsulate the logic for exporting traffic data when grouped by "keyword".
 * Returns a function to determine if the export request should be intercepted and a function to trigger the export.
 */
export const useTopicExportInjection = (): {
  /**
   * Builds and sends the export request to the server which is then queued in the downloads queue.
   */
  exportTopicData: (params: TopicExportParams) => Promise<TopicExportResponse>;
  /**
   * Determines if the export request should be intercepted based on the group by option.
   */
  shouldIntercept: (groupBy: string) => boolean;
} => {
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const { startWeek, endWeek } = mainTimePeriod;
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = comparisonTimePeriod;
  const tab = useAppSelector((state) => state.app.queryParams.tab);
  const subtab = useAppSelector((state) => state.app.queryParams.subtab);
  const fileName = useExportFileName();
  const baseRequest = useBaseTopicRequestBuilder(true) as TopicExportRequestBuilder;
  const appName = getAppName();

  const shouldInterceptRequest = useMemo(() => {
    const isTrafficTab = [BEACON_TABS.TRAFFIC].includes(tab);
    const isAllowedTrafficSubtab = [
      BEACON_SUBTABS.TOTAL_CLICKS,
      BEACON_SUBTABS.ORGANIC_CLICKS,
      BEACON_SUBTABS.AD_CLICKS
    ].includes(subtab);

    return (
      isTrafficTab &&
      isAllowedTrafficSubtab &&
      shouldShowNewBeacon() &&
      shouldShowBeaconProTopics() &&
      appName === AppName.Beacon
    );
  }, [tab, subtab, appName]);

  /**
   * Only intercepts the export request if the group by option is "searchKeyword" or "keyword".
   * and the tab is "Traffic" and the subtab is "Total Clicks", "Organic Clicks", or "Ad Clicks".
   * and the user is on Beacon Pro and the user has access to the Beacon Pro topics.
   */
  const shouldIntercept = useCallback(
    (groupBy: string): boolean => {
      return shouldInterceptRequest && ['searchKeyword', ExportDropdownOptionIds.KEYWORD].includes(groupBy);
    },
    [shouldInterceptRequest]
  );

  const exportTopicData = useCallback(
    async ({ isSeparatedByWeek, isIncludeComparison }: TopicExportParams) => {
      switch (subtab) {
        case BEACON_SUBTABS.AD_CLICKS:
          if (isSeparatedByWeek) {
            baseRequest.setExportType(TrafficSubtabExportTypes.AD_CLICKS_BY_WEEK as unknown as BeaconTopicExportType);
          } else {
            baseRequest.setExportType(TrafficSubtabExportTypes.AD_CLICKS as unknown as BeaconTopicExportType);
          }
          break;

        case BEACON_SUBTABS.TOTAL_CLICKS:
          baseRequest.setExportType(TrafficSubtabExportTypes.TOTAL_CLICKS as unknown as BeaconTopicExportType);
          baseRequest.setSortField('totalTraffic');
          break;

        case BEACON_SUBTABS.ORGANIC_CLICKS:
          baseRequest.setExportType(TrafficSubtabExportTypes.ORGANIC_CLICKS as unknown as BeaconTopicExportType);
          baseRequest.setSortField('organicTraffic');
          break;

        default:
      }

      if (isSeparatedByWeek) {
        baseRequest.setShouldGroupByWeek(true);
      }

      if (isIncludeComparison) {
        baseRequest.setComparisonStartWeekId(comparisonStartWeek);
        baseRequest.setComparisonEndWeekId(comparisonEndWeek);
      }

      baseRequest.setFileName(fileName).setStartWeekId(startWeek).setEndWeekId(endWeek);
      const request = baseRequest.build();

      return topicSettingsServerProxy.exportTopic(request);
    },
    [comparisonEndWeek, comparisonStartWeek, fileName, startWeek, endWeek, subtab, baseRequest]
  );

  return {
    shouldIntercept,
    exportTopicData
  };
};
